import { useEffect, useMemo, useState } from 'react';
import {
  REASON_REQUIRED_MESSAGE,
  nonBillableReasonRequired,
  type AgentSummary,
  type TicketSummary,
} from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import {
  ALL_BUCKETS,
  TICKETLESS_BILLABLE_BUCKETS,
  TICKETLESS_BUCKETS,
  derivedBucketForTicketType,
  type BillingBucket,
} from '../timer/buckets.ts';
import { Button } from './Button.tsx';
import { FormField } from './FormField.tsx';
import { Select } from './Select.tsx';

interface OrgOption {
  id: string;
  name: string;
}

// Subset of fields the dialog needs from a time entry. ticketId is
// nullable because internal (ticketless) entries have ticket_id = NULL.
// The shared TimeEntry type declares ticketId as string, which is wrong
// for internal entries — we use this local type to avoid the lie.
export interface EditableEntry {
  id: string;
  ticketId: string | null;
  agentId: string;
  agentName: string | null;
  startedAt: string;
  durationMinutes: number;
  notes: string | null;
  billingBucket: BillingBucket;
  voidedAt: string | null;
  ticketDisplayNumber?: number | null;
  ticketSubject?: string | null;
}

interface Props {
  mode: 'add' | 'edit';
  isAdmin: boolean;
  meId: string;
  // When provided, the dialog opens pre-filled for this ticket and the
  // user cannot un-pin from the ticket. Useful for the ticket-page "Add".
  pinnedTicket?: { id: string; displayNumber: number; subject: string } | null;
  initial?: EditableEntry;
  onSaved: () => void | Promise<void>;
  onClose: () => void;
}

function nowLocalIso(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export function TimeEntryEditDialog({
  mode,
  isAdmin,
  meId,
  pinnedTicket,
  initial,
  onSaved,
  onClose,
}: Props): React.ReactElement {
  const editing = mode === 'edit' && initial;

  // Type: ticketed vs internal. Pinned ticket forces ticketed.
  const initialIsTicketed = editing
    ? initial!.ticketId !== null
    : pinnedTicket
      ? true
      : true;
  const [isTicketed, setIsTicketed] = useState(initialIsTicketed);

  const [ticketId, setTicketId] = useState<string>(
    pinnedTicket?.id ?? initial?.ticketId ?? '',
  );
  const [agentId, setAgentId] = useState<string>(initial?.agentId ?? meId);
  const [startedAt, setStartedAt] = useState<string>(
    editing ? isoToLocalInput(initial!.startedAt) : nowLocalIso(),
  );
  const [hours, setHours] = useState<string>(
    editing ? String(Math.floor(initial!.durationMinutes / 60)) : '0',
  );
  const [minutes, setMinutes] = useState<string>(
    editing ? String(initial!.durationMinutes % 60) : '30',
  );
  const [notes, setNotes] = useState<string>(initial?.notes ?? '');
  const [bucket, setBucket] = useState<BillingBucket | ''>(
    editing ? initial!.billingBucket : '',
  );
  const [bucketOverride, setBucketOverride] = useState<boolean>(
    editing ? false : false,
  );
  // Org attribution for ticketless billable buckets (block_hours / project).
  // Defaults to the ticket's org on edit if we have one; empty on add.
  const [organizationId, setOrganizationId] = useState<string>('');

  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [orgs, setOrgs] = useState<OrgOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Esc to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // Load ticket options when we need a picker.
  useEffect(() => {
    if (!isTicketed || pinnedTicket) return;
    if (tickets !== null) return;
    apiFetch<{ tickets: TicketSummary[] }>('/api/tickets?closed=false&limit=300')
      .then((r) => setTickets(r.tickets))
      .catch(() => setTickets([]));
  }, [isTicketed, pinnedTicket, tickets]);

  // Load agent options for admin.
  useEffect(() => {
    if (!isAdmin) return;
    if (agents !== null) return;
    apiFetch<{ agents: AgentSummary[] }>('/api/agents')
      .then((r) => setAgents(r.agents.filter((a) => a.isActive)))
      .catch(() => setAgents([]));
  }, [isAdmin, agents]);

  // Load org options whenever the user picks a ticketless billable bucket.
  // Only relevant on add — on edit the org is already attached to the
  // entry and we don't allow re-attributing it from this dialog.
  const needsOrg =
    mode === 'add' && !isTicketed && !!bucket && TICKETLESS_BILLABLE_BUCKETS.has(bucket);
  useEffect(() => {
    if (!needsOrg) return;
    if (orgs !== null) return;
    apiFetch<{ organizations: OrgOption[] }>('/api/organizations')
      .then((r) => setOrgs(r.organizations.map((o) => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgs([]));
  }, [needsOrg, orgs]);

  const durationMinutes = useMemo(() => {
    const h = Math.max(0, Number(hours) || 0);
    const m = Math.max(0, Number(minutes) || 0);
    return h * 60 + m;
  }, [hours, minutes]);

  // Type toggle (Ticketed vs Internal) is available to every agent so
  // techs can log recurring advising / project meetings against a client
  // org without a parent ticket. Pinned-ticket entries stay locked.
  const canChangeType = !pinnedTicket;
  // Non-admins editing their own non-block_hours entry can change date /
  // duration / notes. Admins can change everything.
  const canChangeAgent = isAdmin;
  const canChangeBucket = isAdmin;
  const canChangeTicket = isAdmin && !pinnedTicket;

  // Non-billable time must say what it was for, on a ticket or not. Mirrors
  // the server: on edit it is only enforced when the save changes the notes,
  // ticket or category, so an entry logged before the rule still saves. A
  // ticketed add with no category bills as the ticket's type; a pinned ticket
  // carries no type, so there the server's 400 is the check.
  const pickedTicketType = isTicketed ? (tickets?.find((t) => t.id === ticketId)?.type ?? null) : null;
  const reasonRequired = nonBillableReasonRequired(
    bucket || derivedBucketForTicketType(pickedTicketType),
  );
  const reasonEnforced =
    reasonRequired &&
    (mode === 'add' ||
      notes.trim() !== (initial?.notes ?? '').trim() ||
      bucket !== initial?.billingBucket ||
      (initial?.ticketId ?? null) !== (isTicketed ? ticketId || null : null));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (durationMinutes < 1 || durationMinutes > 24 * 60) {
      setError('Duration must be at least 1 minute and at most 24 hours.');
      return;
    }
    if (isTicketed && !ticketId) {
      setError('Pick a ticket.');
      return;
    }
    if (!isTicketed) {
      if (!bucket || !TICKETLESS_BUCKETS.includes(bucket)) {
        setError('Pick a category.');
        return;
      }
      if (mode === 'add' && TICKETLESS_BILLABLE_BUCKETS.has(bucket) && !organizationId) {
        setError('Pick the client organization this time is for.');
        return;
      }
    }
    if (reasonEnforced && !notes.trim()) {
      setError(REASON_REQUIRED_MESSAGE);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (mode === 'add') {
        if (isTicketed) {
          await apiFetch('/api/timesheets', {
            method: 'POST',
            json: {
              ticketId,
              startedAt: new Date(startedAt).toISOString(),
              durationMinutes,
              notes: notes.trim() || undefined,
              ...(canChangeAgent && agentId !== meId ? { agentId } : {}),
              ...(canChangeBucket && bucketOverride && bucket ? { billingBucket: bucket } : {}),
            },
          });
        } else {
          await apiFetch('/api/timesheets/internal', {
            method: 'POST',
            json: {
              bucket,
              startedAt: new Date(startedAt).toISOString(),
              durationMinutes,
              notes: notes.trim() || undefined,
              ...(canChangeAgent && agentId !== meId ? { agentId } : {}),
              ...(bucket && TICKETLESS_BILLABLE_BUCKETS.has(bucket) && organizationId
                ? { organizationId }
                : {}),
            },
          });
        }
      } else {
        // edit
        const body: Record<string, unknown> = {
          startedAt: new Date(startedAt).toISOString(),
          durationMinutes,
          notes: notes.trim() ? notes.trim() : null,
        };
        if (canChangeAgent && agentId !== initial!.agentId) body.agentId = agentId;
        if (canChangeBucket && bucket && bucket !== initial!.billingBucket) {
          body.billingBucket = bucket;
        }
        if (canChangeTicket) {
          const newTicketId = isTicketed ? ticketId : null;
          if (newTicketId !== initial!.ticketId) body.ticketId = newTicketId;
        }
        await apiFetch(`/api/timesheets/${initial!.id}`, {
          method: 'PATCH',
          json: body,
        });
      }
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl rounded-[var(--radius-card)] bg-white shadow-xl">
        <div className="border-b border-[var(--color-border)] px-6 py-4">
          <h2 className="text-lg font-bold text-[var(--color-header)]">
            {mode === 'add' ? 'Add time entry' : 'Edit time entry'}
          </h2>
          {isAdmin && mode === 'add' && (
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Admin: you can log on behalf of any agent and override the billing bucket.
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          {canChangeType && (
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={isTicketed}
                  onChange={() => setIsTicketed(true)}
                  className="h-4 w-4 accent-[var(--color-t3-blue)]"
                />
                Ticketed
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={!isTicketed}
                  onChange={() => setIsTicketed(false)}
                  className="h-4 w-4 accent-[var(--color-t3-blue)]"
                />
                No ticket
              </label>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {isTicketed ? (
              pinnedTicket ? (
                <div>
                  <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">Ticket</span>
                  <div className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm">
                    T-{pinnedTicket.displayNumber} · {pinnedTicket.subject.slice(0, 60)}
                  </div>
                </div>
              ) : (
                <Select
                  label="Ticket"
                  value={ticketId}
                  onChange={(e) => setTicketId(e.target.value)}
                  required
                  disabled={!canChangeTicket && mode === 'edit'}
                >
                  <option value="">— pick one —</option>
                  {(tickets ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      T-{t.displayNumber} · {t.organizationName} · {t.subject.slice(0, 50)}
                    </option>
                  ))}
                </Select>
              )
            ) : (
              <>
                <Select
                  label="Category"
                  value={bucket}
                  onChange={(e) => {
                    const next = e.target.value as BillingBucket | '';
                    setBucket(next);
                    if (!next || !TICKETLESS_BILLABLE_BUCKETS.has(next)) {
                      setOrganizationId('');
                    }
                  }}
                  required
                >
                  <option value="">— pick one —</option>
                  {ALL_BUCKETS.filter((b) => b.inTicketless).map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </Select>
                {needsOrg && (
                  <Select
                    label="Client organization"
                    value={organizationId}
                    onChange={(e) => setOrganizationId(e.target.value)}
                    required
                  >
                    <option value="">— pick one —</option>
                    {(orgs ?? []).map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </Select>
                )}
              </>
            )}

            {canChangeAgent && (
              <Select
                label="Agent"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                required
              >
                {(agents ?? []).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
                {agents === null && initial && (
                  <option value={initial.agentId}>{initial.agentName ?? 'Loading…'}</option>
                )}
              </Select>
            )}

            <FormField
              label="Started at"
              name="startedAt"
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              required
            />

            <div>
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">Duration</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={24}
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  className="w-20 rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2"
                />
                <span className="text-sm text-[var(--color-muted)]">h</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  className="w-20 rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2"
                />
                <span className="text-sm text-[var(--color-muted)]">m</span>
                <span className="text-xs text-[var(--color-muted)]">
                  = {durationMinutes} min
                </span>
              </div>
            </div>
          </div>

          {isTicketed && canChangeBucket && (
            <div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={bucketOverride || (mode === 'edit' && !!bucket && bucket !== initial?.billingBucket)}
                  onChange={(e) => {
                    setBucketOverride(e.target.checked);
                    if (!e.target.checked && mode === 'edit') setBucket(initial!.billingBucket);
                    if (!e.target.checked && mode === 'add') setBucket('');
                  }}
                  className="h-4 w-4 accent-[var(--color-t3-blue)]"
                />
                <span>Override billing bucket</span>
                <span className="text-xs text-[var(--color-muted)]">
                  (default: derived from ticket type)
                </span>
              </label>
              {(bucketOverride || (mode === 'edit' && bucket && bucket !== initial?.billingBucket)) && (
                <Select
                  label=""
                  value={bucket}
                  onChange={(e) => setBucket(e.target.value as BillingBucket)}
                  className="mt-2"
                >
                  <option value="">— pick one —</option>
                  {ALL_BUCKETS.filter((b) => b.inTicketed).map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </Select>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-header)]" htmlFor="notes">
              {reasonRequired ? 'Reason (required)' : 'Notes (optional)'}
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={reasonRequired ? 'What is this time for?' : undefined}
              className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
            />
          </div>

          {error && (
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] pt-4">
            <Button variant="outline" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : mode === 'add' ? 'Add entry' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
